import { Grid, Stack, Typography } from '@mui/material'
import { VesselKPIUIStore } from '@operations/features/vesselVisitDashboard/stores/vessel-kpi.ui-store'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { VesselVisitKPIStats } from '../../models/vessel-kpi'
import { VesselVisitMoveStats } from '../molecules/VesselVisitMoveStats'
import { VesselVisitOrderStats } from '../molecules/VesselVisitOrderStats'

interface Props {
  visitId: number
  isCompleted: boolean
  vesselVisitKPIStats: VesselVisitKPIStats
  vesselKPIUIStore: VesselKPIUIStore
}

export const VesselVisitKPISummary = observer(
  ({ visitId, isCompleted, vesselVisitKPIStats, vesselKPIUIStore }: Props) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()

    const { discharge, load, restow } = vesselVisitKPIStats || {
      discharge: { total: 0, completed: 0, planned: 0 },
      load: { total: 0, completed: 0, planned: 0 },
      restow: { total: 0, completed: 0, planned: 0 },
    }
    const moves = vesselKPIUIStore.visitPerformance?.moves || {
      total: 0,
      executed: 0,
      remaining: 0,
    }
    const hatchCoverMoves = vesselKPIUIStore.visitPerformance?.extraMoves?.hatchCover || 0
    const oogMoves = vesselKPIUIStore.visitPerformance?.extraMoves?.oog || 0

    return (
      <Stack gap={theme.customSpacing.m}>
        <Stack direction='row' gap={theme.customSpacing.s}>
          <Stack flex={2} gap={theme.customSpacing.s}>
            <Typography variant='h4'>{t('kpis', 'KPIs')}</Typography>
            <Grid container spacing={theme.customSpacing.s} size={12}>
              <Grid size='grow'>
                <VesselVisitOrderStats
                  type='discharge'
                  title={t('discharge', 'Discharge')}
                  isCompleted={isCompleted}
                  stats={discharge}
                />
              </Grid>
              <Grid size='grow'>
                <VesselVisitOrderStats
                  type='load'
                  title={t('load', 'Load')}
                  isCompleted={isCompleted}
                  stats={load}
                />
              </Grid>
              <Grid size='grow'>
                <VesselVisitOrderStats
                  type='restow'
                  title={t('restow', 'Restow')}
                  isCompleted={isCompleted}
                  stats={restow}
                />
              </Grid>
              <Grid size='grow'>
                <VesselVisitOrderStats
                  type='moves'
                  title={t('moves', 'Moves')}
                  isCompleted={isCompleted}
                  stats={{
                    total: moves?.total,
                    completed: moves?.executed,
                    planned: moves?.remaining,
                  }}
                />
              </Grid>
              {isCompleted && (
                <Grid size='grow'>
                  <VesselVisitOrderStats
                    type='teu'
                    title={t('teus', 'TEUs')}
                    isCompleted={isCompleted}
                  />
                </Grid>
              )}
            </Grid>
          </Stack>

          {isCompleted && (
            <Stack flex={1} gap={theme.customSpacing.s} alignSelf='flex-end'>
              <Typography variant='captionBold'>{t('extraMoves', 'Extra moves')} </Typography>
              <Stack flex={1} direction='row' gap={theme.customSpacing.s}>
                <Stack flex={1}>
                  <VesselVisitOrderStats
                    type='hatchCover'
                    title={t('hatchCover', 'Hatch Cover')}
                    isCompleted={isCompleted}
                    stats={{ completed: hatchCoverMoves }}
                  />
                </Stack>
                <Stack flex={1}>
                  <VesselVisitOrderStats
                    type='oog'
                    title={t('oog', 'OOG')}
                    isCompleted={isCompleted}
                    stats={{ completed: oogMoves }}
                  />
                </Stack>
              </Stack>
            </Stack>
          )}
        </Stack>
        {!isCompleted && (
          <Stack>
            <Grid size={12} container spacing={theme.customSpacing.s}>
              <Grid size='grow'>
                <VesselVisitMoveStats
                  type='avgGrossProductivity'
                  title={t('averageGrossProductivity', 'Average Gross Productivity')}
                  subtitle={t('movesPerHour', 'Moves per hour')}
                  value={
                    vesselKPIUIStore.visitPerformance?.performance &&
                    Math.round(vesselKPIUIStore.visitPerformance?.performance)
                  }
                />
              </Grid>
              <Grid size='grow'>
                <VesselVisitMoveStats
                  type='estimatedHours'
                  title={t('estimatedRemainingHours', 'Estimated remaining hours')}
                  subtitle={t('hours', 'Hours')}
                  value={vesselKPIUIStore.hoursLeft}
                />
              </Grid>
              <Grid size={6}>
                <VesselVisitMoveStats
                  type='firstMove'
                  title={t('firstMove', 'First Move')}
                  subtitle={t('movesPerHour', 'Moves per hour')}
                  value={{
                    discharge: vesselKPIUIStore.visitPerformance?.firstInboundDate,
                    load: vesselKPIUIStore.visitPerformance?.firstOutboundDate,
                  }}
                />
              </Grid>
            </Grid>
          </Stack>
        )}
      </Stack>
    )
  },
)
